// nav-gating.js — hide nav items + page sections when the user
// lacks the required scope.
//
// Added in migration 037 (page-level read scopes). The 5 page-level
// scopes are: read:overview, read:chats, read:files, read:analytics,
// read:agents. Each corresponding nav item (and the .page section
// it routes to) carries a `data-scope` attribute. This module reads
// `teamState.info.your_scopes` (server-resolved effective scope
// list, with `*` already expanded to ALL_SCOPES) and toggles a
// `nav-gated` class that CSS hides via `display: none`.
//
// Gating is defense-in-depth UI affordance only — the server is the
// authoritative gate. A user with dev-tools could un-hide a nav
// item but the corresponding /api/* endpoints would still 403 on
// their token's scope set.
//
// Solo / unauthenticated mode: when teamState.info is null (no
// coordinator hookup), every page is visible (no gating). This
// matches the local-daemon UX where there's no concept of "team
// roles" — the user owns the whole daemon.
//
// Direct URL access: if a user types /analytics into the address
// bar but lacks read:analytics, router.js's navigate() still flips
// the section to .active and the section's CSS rule wins ("the
// .nav-gated rule has higher specificity"). They see a blank pane
// rather than the page content. A future polish could intercept
// navigate() and redirect to overview with a toast; not landed in
// this PR.

import { teamState } from "./page-state.js";

// applyNavGating reads teamState.info.your_scopes and applies the
// .nav-gated class to nav items + page sections the caller lacks
// the scope for. Idempotent — running twice produces the same DOM
// state. Safe to call on every teamState.info update.
export function applyNavGating() {
  // Build the granted scope set. Empty = no info yet OR solo mode.
  // Both render every page (no gating).
  const info = teamState.info;
  const granted = new Set(
    info && Array.isArray(info.your_scopes) ? info.your_scopes : []
  );
  const hasInfo = !!info;

  // Two element types carry data-scope: .nav-item (sidebar buttons)
  // and section.page (the actual page content). Pre-gating both is
  // important — without the .page rule a user routed to a gated
  // page via URL would briefly see its content before the next
  // render cycle hides it.
  //
  // The selector is class-scoped because team-roles.js's role editor
  // ALSO writes data-scope onto its <input type="checkbox"> elements
  // (one per scope in SCOPE_VOCAB) — a bare [data-scope] selector
  // would collide and hide every scope checkbox the user doesn't
  // currently hold, breaking the Roles editor for non-admins.
  // Sweep round 1 caught the collision.
  // v0.32.6: treat "*" as wildcard-grants-all. Pre-fix, the daemon's
  // soloTeamsInfo emitted `your_scopes: ["*"]` to denote "you own
  // everything in solo mode," but this function did exact-match
  // lookup against the Set — `granted.has("read:overview")` returned
  // false against a set containing only `"*"`, so EVERY data-scoped
  // nav item got .nav-gated and the dashboard rendered blank for the
  // solo user. Short-circuit the wildcard up front.
  const hasWildcard = granted.has("*");
  const allGated = document.querySelectorAll(".nav-item[data-scope], section.page[data-scope]");
  allGated.forEach((el) => {
    const required = el.getAttribute("data-scope") || "";
    if (!required) return;
    // No team info → show everything (solo / unauthenticated mode).
    // Has team info but no your_scopes field → server is pre-PR-3;
    // show everything (back-compat).
    // your_scopes contains "*" → wildcard, show everything.
    const shouldHide = hasInfo
      && Array.isArray(info.your_scopes)
      && !hasWildcard
      && !granted.has(required);
    el.classList.toggle("nav-gated", shouldHide);
  });

  // Roles nav gate: solo Free-tier teams (1 human, no paid plan)
  // don't need role management — there's no one to assign roles to
  // and they can't invite without upgrading. Hide the Roles nav item
  // until either (a) the team has >1 distinct human OR (b) the team
  // is on a paid tier (in which case role pre-configuration before
  // inviting makes sense).
  //
  // Uses user_count (distinct humans in user_teams) NOT member_count
  // (active install tokens — one user with 2 laptops = 2). Sweep
  // round caught this footgun: a solo user with multiple laptops
  // would have member_count=2 and see Roles despite being solo.
  //
  // Fail-open on a missing user_count field: a typeof !== "number"
  // means an older server didn't ship this field; we show Roles
  // rather than hide it.
  const rolesNav = document.querySelector('.nav-item[data-page="roles"]');
  if (rolesNav) {
    const userCount = hasInfo && typeof info.user_count === "number"
      ? info.user_count
      : 999; // unknown → show
    const isFreeTier = hasInfo && info.tier === "solo";
    const shouldHide = hasInfo && isFreeTier && userCount <= 1;
    rolesNav.classList.toggle("nav-gated", shouldHide);
  }
}
